<template>
  <div class="bandeira">
    <!--
      O mastro existe mesmo sem pano. Um clã sem bandeira com um buraco no lugar
      dela pareceria erro de carregamento; um mastro vazio parece o que é — um
      lugar que ainda não tem cara.
    -->
    <div class="bandeira__mastro" :class="{ 'bandeira__mastro--vazio': !atual }">
      <img v-if="atual" class="bandeira__pano" :src="atual" :alt="$t('clan.bandeira.alt')" />
      <FishIcon v-else nome="anchor" tamanho="grande" />
    </div>

    <button v-if="isLeader" type="button" class="bandeira__trocar" @click="alternar">
      {{ aberto ? $t('clan.bandeira.fechar') : $t('clan.bandeira.trocar') }}
    </button>

    <!-- ===================== o escolhedor, só do líder ===================== -->
    <div v-if="aberto && isLeader" class="bandeira__escolha">
      <p class="bandeira__rotulo">{{ $t('clan.bandeira.prontas') }}</p>

      <!--
        As prontas vêm antes do envio porque são o caminho de todo dia: o D25
        as criou para o clã ter identidade **desde o primeiro dia**, sem depender
        de alguém ter um PNG à mão.
      -->
      <ul class="bandeira__prontas">
        <li v-for="pronta in prontas" :key="pronta.id">
          <button
            type="button"
            class="bandeira__pronta"
            :class="{ 'bandeira__pronta--atual': pronta.imagem === atual }"
            :title="pronta.nome"
            :disabled="salvando"
            @click="escolher(pronta)"
          >
            <img :src="pronta.imagem" :alt="pronta.nome" />
          </button>
        </li>
      </ul>

      <p class="bandeira__rotulo">{{ $t('clan.bandeira.sua') }}</p>
      <p class="bandeira__regra">
        {{ $t('clan.bandeira.regra', { mb: TETO_EM_MB, lado: LADO_MAXIMO }) }}
      </p>

      <input
        ref="arquivo"
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        class="bandeira__arquivo"
        :disabled="salvando"
        @change="enviar"
      />

      <FishButton
        v-if="atual"
        tamanho="pequeno"
        variante="perigo"
        :desabilitado="salvando"
        @click="remover"
      >
        {{ $t('clan.bandeira.remover') }}
      </FishButton>

      <p v-if="erro" class="bandeira__erro">{{ erro }}</p>
    </div>
  </div>
</template>

<script>
import { FishButton, FishIcon } from '@/shared/ui';
import * as v2 from '@/core/services/api/v2';

/**
 * A BANDEIRA DO CLÃ (D25, 7.4g/7.4q).
 *
 * É a **única superfície do Fishbay em que um jogador publica imagem para
 * estranhos** — ela aparece no quadro de honra, ao lado dos membros, no cais de
 * quem nem é do clã. Daí tudo o que a cerca ser diferente do resto do jogo: teto
 * de 2 MB, limite de lado, SVG recusado, e uma fila de moderação no painel.
 *
 * Duas origens, **um dono só**: prontas e envio terminam os dois em
 * `clans.flag_media_id`. Ter dois campos para a mesma ideia é o erro que o D22
 * documentou em `type` e `releasedForAll`, e que custou uma seção inteira.
 *
 * O componente **mostra para todo mundo e edita só para o líder**: a bandeira é
 * do clã, e o membro precisa vê-la tanto quanto quem a escolheu.
 */

/* Espelham o servidor (`clan-bandeira.service.ts`), e aqui só viram frase: quem
 * recusa é ele, e a mensagem dele é a que aparece no lugar do erro genérico. */
const TETO_EM_MB = 2;
const LADO_MAXIMO = 1024;

export default {
  name: 'ClanBandeira',
  components: { FishButton, FishIcon },
  props: {
    isLeader: { type: Boolean, default: false },
    /** A bandeira que o clã já tem, do `detalhesDoCla`. */
    inicial: { type: String, default: null },
  },
  emits: ['erro', 'trocou'],
  data() {
    return {
      TETO_EM_MB,
      LADO_MAXIMO,
      atual: this.inicial,
      prontas: [],
      aberto: false,
      salvando: false,
      erro: '',
    };
  },
  watch: {
    inicial(valor) {
      this.atual = valor;
    },
  },
  methods: {
    async alternar() {
      this.erro = '';
      this.aberto = !this.aberto;
      if (this.aberto && !this.prontas.length) {
        try {
          const r = await v2.bandeirasProntas(this.$store.state.player.id);
          this.prontas = r.itens || [];
        } catch (error) {
          this.$emit('erro', error);
        }
      }
    },

    /** Toda troca devolve a bandeira nova; quem chama nunca recarrega o clã. */
    aplicar(resposta) {
      this.atual = resposta?.imagem ?? null;
      this.$emit('trocou', this.atual);
    },

    async escolher(pronta) {
      this.salvando = true;
      this.erro = '';
      try {
        this.aplicar(await v2.escolherBandeira(this.$store.state.player.id, pronta.id));
      } catch (error) {
        this.erro = error?.response?.data?.message || this.$t('clan.bandeira.falhou');
      } finally {
        this.salvando = false;
      }
    },

    /**
     * O envio.
     *
     * O campo é limpo no fim **dê no que der**: sem isso, escolher o mesmo
     * arquivo depois de um erro não dispara `change` de novo, e o líder fica
     * clicando num arquivo que o navegador considera já escolhido.
     */
    async enviar(evento) {
      const arquivo = evento.target.files?.[0];
      if (!arquivo) return;

      this.salvando = true;
      this.erro = '';
      try {
        this.aplicar(await v2.enviarBandeira(this.$store.state.player.id, arquivo));
      } catch (error) {
        // A mensagem do servidor é a boa: ela diz 1200x900 quando o limite é
        // 1024, e um "não deu certo" genérico esconderia justamente isso.
        this.erro = error?.response?.data?.message || this.$t('clan.bandeira.falhou');
      } finally {
        this.salvando = false;
        if (this.$refs.arquivo) this.$refs.arquivo.value = '';
      }
    },

    async remover() {
      this.salvando = true;
      this.erro = '';
      try {
        this.aplicar(await v2.removerBandeira(this.$store.state.player.id));
      } catch (error) {
        this.erro = error?.response?.data?.message || this.$t('clan.bandeira.falhou');
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>

<style scoped>
.bandeira {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-1);
}

/* O mastro é uma moldura de madeira; o pano preenche o que couber dentro dela. */
.bandeira__mastro {
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--madeira);
  background: var(--papel-sombra);
  overflow: hidden;
}

.bandeira__mastro--vazio {
  color: var(--tinta-fraca);
  border-style: dashed;
}

.bandeira__pano {
  width: 100%;
  height: 100%;
  object-fit: contain;
  image-rendering: pixelated;
}

.bandeira__trocar {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: var(--f-1);
  color: var(--latao);
  text-decoration: underline dashed;
  cursor: pointer;
}

.bandeira__escolha {
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  align-items: flex-start;
  width: 100%;
}

.bandeira__rotulo {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.bandeira__regra {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.bandeira__prontas {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--e-1);
}

.bandeira__pronta {
  width: 44px;
  height: 44px;
  padding: 0;
  border: 2px solid var(--madeira);
  background: var(--papel);
  cursor: pointer;
}

.bandeira__pronta--atual {
  border-color: var(--latao);
}

.bandeira__pronta img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  image-rendering: pixelated;
}

.bandeira__arquivo {
  font: inherit;
  font-size: var(--f-1);
  color: var(--tinta);
  max-width: 100%;
}

.bandeira__erro {
  margin: 0;
  font-size: var(--f-1);
  color: var(--ruim, #a33);
}
</style>
